import { useState } from 'react';
import type { EvidenceItem } from './buildPortalViewModel';

interface EvidencePanelProps {
  items: EvidenceItem[];
  onItemClick?: (itemId: string) => void;
}

export function EvidencePanel({ items, onItemClick }: EvidencePanelProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  const confirmed = items.filter(i => i.status === 'confirmed');
  const assumed = items.filter(i => i.status === 'assumed');
  const unknown = items.filter(i => i.status === 'unknown');

  const handleItemClick = (id: string) => {
    setExpandedItem(prev => prev === id ? null : id);
    onItemClick?.(id);
  };

  return (
    <aside
      className="evidence-panel"
      role="complementary"
      aria-label="Evidence and assumptions"
    >
      <button
        className="evidence-panel__toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        aria-controls="evidence-panel-content"
      >
        <h2 className="evidence-panel__heading">How certain are these conclusions?</h2>
        <span className="evidence-panel__toggle-icon" aria-hidden="true">
          {isExpanded ? '\u25B4' : '\u25BE'}
        </span>
      </button>

      {isExpanded && (
        <div id="evidence-panel-content" className="evidence-panel__content">
          <div className="evidence-panel__columns">
            {/* Confirmed */}
            <EvidenceColumn
              title="Confirmed"
              badge="confirmed"
              items={confirmed}
              expandedItem={expandedItem}
              onItemClick={handleItemClick}
            />
            {/* Assumed */}
            <EvidenceColumn
              title="Assumptions used"
              badge="assumed"
              items={assumed}
              expandedItem={expandedItem}
              onItemClick={handleItemClick}
            />
            {/* Unknown */}
            <EvidenceColumn
              title="Unknowns"
              badge="unknown"
              items={unknown}
              expandedItem={expandedItem}
              onItemClick={handleItemClick}
            />
          </div>
        </div>
      )}
    </aside>
  );
}

// ─── Evidence Column ─────────────────────────────────────────────

function EvidenceColumn({ title, badge, items, expandedItem, onItemClick }: {
  title: string;
  badge: 'confirmed' | 'assumed' | 'unknown';
  items: EvidenceItem[];
  expandedItem: string | null;
  onItemClick: (id: string) => void;
}) {
  if (items.length === 0) return null;

  return (
    <div className="evidence-column">
      <h3 className="evidence-column__title">
        <span className={`evidence-column__badge evidence-column__badge--${badge}`} aria-hidden="true" />
        {title}
        <span className="evidence-column__count">({items.length})</span>
      </h3>
      <ul className="evidence-column__list" role="list">
        {items.map(item => (
          <EvidenceItemRow
            key={item.id}
            item={item}
            isExpanded={expandedItem === item.id}
            onClick={() => onItemClick(item.id)}
          />
        ))}
      </ul>
    </div>
  );
}

// ─── Evidence Item ───────────────────────────────────────────────

function EvidenceItemRow({ item, isExpanded, onClick }: {
  item: EvidenceItem;
  isExpanded: boolean;
  onClick: () => void;
}) {
  const hasDetail = !!(item.reason || item.ifWrong || item.value);

  return (
    <li className="evidence-item">
      <button
        className="evidence-item__button"
        onClick={onClick}
        aria-expanded={hasDetail ? isExpanded : undefined}
        disabled={!hasDetail}
      >
        <span className="evidence-item__label">{item.label}</span>
        {item.value && (
          <span className="evidence-item__value">{item.value}</span>
        )}
        {hasDetail && (
          <span className="evidence-item__chevron" aria-hidden="true">
            {isExpanded ? '\u25B4' : '\u25BE'}
          </span>
        )}
      </button>
      {isExpanded && hasDetail && (
        <div className="evidence-item__detail">
          {item.reason && (
            <p className="evidence-item__reason">
              <strong>Why assumed:</strong> {item.reason}
            </p>
          )}
          {item.ifWrong && (
            <p className="evidence-item__if-wrong">
              <strong>If wrong:</strong> {item.ifWrong}
            </p>
          )}
        </div>
      )}
    </li>
  );
}
